/**
 * The competency catalog, and the way to what is assigned to you.
 *
 * Two audiences on one page: staff, who mostly want "what do I owe", and
 * the people who define competencies. The banner at the top serves the
 * first; the table serves the second.
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { hasAtLeast, ROLE_DISPLAY_NAMES, isRole } from '../../types/roles';
import { FILTER_MAX } from '../../types/fieldLimits';
import { useAddons } from '../org/useAddons';
import { useTaxonomy } from '../admin/useTaxonomy';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { SegmentedChoice } from '../../shared/SegmentedChoice';
import { isCallFailure } from '../../lib/callable';
import { formatDay } from '../../shared/dates';
import { useCompetencies, useMyAssignments } from './useCompetencies';
import { COMPETENCY_STATUSES, STATUS_LABEL, STATUS_TONE, type CompetencyStatus, type CompetencySummary } from './types';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Competencies.module.css';

export const LOCKED_TITLE = 'Competency and training is not turned on';

export function appliesText(c: CompetencySummary, departmentName: (id: string) => string): string {
  const roles = c.appliesToRoles.map((r) => (isRole(r) ? ROLE_DISPLAY_NAMES[r] : r));
  const depts = c.appliesToDepartments.map(departmentName).filter(Boolean);
  if (!roles.length && !depts.length) return 'Everyone';
  return [...roles, ...depts].join(', ');
}

export function CompetencyListPage() {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const uid = session?.uid ?? null;
  const roles = session?.roles ?? [];
  const author = hasAtLeast(roles, 'author');

  const addons = useAddons(orgId);
  const enabled = addons.data?.competencyEvidence === true;
  const list = useCompetencies(orgId, enabled);
  const mine = useMyAssignments(orgId, uid, enabled);
  const departments = useTaxonomy('department', enabled);
  const departmentName = useMemo(() => {
    const byId = new Map((departments.data ?? []).map((d) => [d.id, d.name]));
    return (id: string) => byId.get(id) ?? '';
  }, [departments.data]);

  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const statusParam = params.get('status');
  const status: CompetencyStatus | 'all' = (COMPETENCY_STATUSES as readonly string[]).includes(statusParam ?? '') ? (statusParam as CompetencyStatus) : 'all';
  const setFilter = (next: { q?: string; status?: CompetencyStatus | 'all' }) => {
    const p = new URLSearchParams();
    const nq = next.q ?? q; const ns = next.status ?? status;
    if (nq.trim()) p.set('q', nq);
    if (ns !== 'all') p.set('status', ns);
    setParams(p, { replace: true });
  };

  const rows = list.data ?? [];
  const shown = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((c) => {
      if (status !== 'all' && c.status !== status) return false;
      if (!words.length) return true;
      const hay = `${c.title} ${c.description ?? ''} ${appliesText(c, departmentName)}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [rows, q, status, departmentName]);
  const filtered = q.trim() !== '' || status !== 'all';
  const due = (mine.data ?? []).filter((a) => a.status === 'pending' || a.status === 'overdue');
  const overdue = due.filter((a) => a.status === 'overdue').length;

  if (addons.isLoading) return <Spinner label="Loading" />;
  if (!enabled) {
    return (
      <>
        <header className={styles.header}><h1 className={styles.title}>Competency and training</h1></header>
        <div className={styles.body}>
          <PageState title={LOCKED_TITLE}>This add-on is not enabled for your organization. Your StandingDocs administrator can turn it on.</PageState>
        </div>
      </>
    );
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Competency and training</h1>
            <p className={styles.subtitle}>
              What staff must be able to do, and the proof that they can. Publishing a competency assigns
              it to everyone it applies to; each person submits evidence by the due date.
            </p>
          </div>
          {author && (
            <div className={styles.actions}>
              <Link to="/competencies/new" className={`${styles.btn} ${styles.btnPrimary}`}>New competency</Link>
            </div>
          )}
        </div>
      </header>

      <div className={styles.body}>
        <div className={own.mine} role="status">
          <p>
            {mine.isLoading ? 'Checking what is assigned to you…'
              : due.length === 0 ? 'Nothing is waiting on you.'
              : <><b className="tnum">{due.length}</b> {due.length === 1 ? 'competency is' : 'competencies are'} assigned to you{overdue > 0 && <>, <b className="tnum">{overdue}</b> overdue</>}.</>}
          </p>
          <Link to="/competencies/mine" className={styles.btn}>Your assignments</Link>
        </div>

        {list.error && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
            Could not load competencies. {isCallFailure(list.error) ? list.error.message : ''}
          </div>
        )}

        {list.isLoading ? (
          <Spinner label="Loading competencies" />
        ) : rows.length === 0 ? (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
            No competencies yet. {author ? 'Define the first with New competency.' : 'An author defines them.'}
          </div>
        ) : (
          <>
            <div className={own.filters}>
              <div className={own.filterText}>
                <label className={form.label} htmlFor="comp-filter">Filter</label>
                <input id="comp-filter" className={form.input} type="text" maxLength={FILTER_MAX} autoComplete="off"
                  value={q} onChange={(e) => setFilter({ q: e.target.value })} placeholder="Title, description, role or department" />
              </div>
              <SegmentedChoice legend="Status" name="comp-status" value={status} onChange={(v) => setFilter({ status: v })}
                options={[{ value: 'all' as const, label: `All (${rows.length})` },
                  ...COMPETENCY_STATUSES.map((s) => ({ value: s, label: `${STATUS_LABEL[s]} (${rows.filter((r) => r.status === s).length})` }))]} />
            </div>

            <p className={own.count} role="status" aria-live="polite">
              <span><b className="tnum">{shown.length}</b> of <span className="tnum">{rows.length}</span></span>
              {filtered && <button type="button" className={own.linkBtn} onClick={() => setParams(new URLSearchParams(), { replace: true })}>Clear filters</button>}
            </p>

            <div className={own.tableWrap}>
              <table className={own.table}>
                <thead>
                  <tr>
                    <th scope="col">Competency</th>
                    <th scope="col">Applies to</th>
                    <th scope="col">Renews</th>
                    <th scope="col">Version</th>
                    <th scope="col">Status</th>
                    <th scope="col">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((c) => (
                    <tr key={c.id}>
                      <td className={own.titleCell}>
                        <Link to={`/competencies/${c.id}`}>{c.title}</Link>
                        {c.description && <span className={own.sub}>{c.description.slice(0, 90)}{c.description.length > 90 ? '…' : ''}</span>}
                      </td>
                      <td>{appliesText(c, departmentName)}</td>
                      <td className={`${own.num} ${c.renewalIntervalDays ? '' : own.muted}`}>{c.renewalIntervalDays ? `every ${c.renewalIntervalDays} days` : '—'}</td>
                      <td className={`${own.num} ${c.currentVersionNumber ? '' : own.muted}`}>{c.currentVersionNumber ? `v${c.currentVersionNumber}` : '—'}</td>
                      <td><span className={`${styles.tag} ${styles[STATUS_TONE[c.status]]}`}>{STATUS_LABEL[c.status]}</span></td>
                      <td className={`${own.num} ${c.updatedAt ? '' : own.muted}`}>{formatDay(c.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shown.length === 0 && <p className={styles.subtitle}>Nothing matches. <strong>Clear filters</strong> to see all {rows.length}.</p>}
            </div>
          </>
        )}
      </div>
    </>
  );
}
